import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './types/database';

/**
 * Client Supabase typé avec le schéma cb-core.
 *
 * Tous les appels `.from()`, `.rpc()`, etc. sont typés automatiquement
 * avec les tables, colonnes, et types du schéma cb-core.
 */
export type CBClient = SupabaseClient<Database>;

/**
 * Options de configuration pour créer un client cb-core.
 */
export interface CreateCBClientOptions {
  /**
   * URL du projet Supabase (ex: https://xxx.supabase.co)
   */
  supabaseUrl: string;

  /**
   * Clé publique anonyme (anon key) du projet Supabase.
   */
  supabaseAnonKey: string;

  /**
   * Identifiant de l'application source.
   *
   * Doit correspondre à un `code` dans la table `apps` de cb-core.
   * Utilisé pour tracer l'origine des modifications dans `audit_log`.
   *
   * @example 'cbimmodoc', 'bestplace', 'playground'
   */
  appSource: string;

  /**
   * Options additionnelles passées directement au client Supabase.
   *
   * Permet de configurer la persistance de session, le timeout, etc.
   */
  supabaseOptions?: Parameters<typeof createClient>[2];
}

/**
 * Crée un client Supabase typé pour interagir avec cb-core.
 *
 * Le client retourné est identique à un SupabaseClient classique,
 * mais avec tous les types du schéma cb-core (tables, colonnes, enums).
 *
 * @param options - Configuration du client
 * @returns Client Supabase typé avec le schéma cb-core
 *
 * @example
 * ```typescript
 * const client = createCBClient({
 *   supabaseUrl: 'https://yoqbqewkmfrcnaxxoahs.supabase.co',
 *   supabaseAnonKey: process.env.SUPABASE_ANON_KEY,
 *   appSource: 'cbimmodoc',
 * });
 *
 * // Les appels sont typés automatiquement
 * const { data: agencies } = await client.from('agencies').select('*');
 * ```
 */
export function createCBClient(options: CreateCBClientOptions): CBClient {
  const { supabaseUrl, supabaseAnonKey, appSource, supabaseOptions } = options;

  // Créer le client Supabase avec le schéma cb-core
  const client = createClient<Database>(supabaseUrl, supabaseAnonKey, {
    ...supabaseOptions,
    auth: {
      // Options personnalisables d'abord
      ...supabaseOptions?.auth,
      // Puis nos options par défaut (prioritaires)
      // Stocker la session dans localStorage (navigateur)
      storage: typeof window !== 'undefined' ? window.localStorage : undefined,
      // Persister la session entre les rechargements
      persistSession: true,
      // Auto-refresh du token avant expiration
      autoRefreshToken: true,
      // ⚠️ CRITIQUE : Détecter et absorber le token dans l'URL après OAuth callback
      detectSessionInUrl: true,
      // Flow OAuth implicite (token dans le hash #access_token=...)
      flowType: 'implicit',
    },
    global: {
      ...supabaseOptions?.global,
      headers: {
        ...supabaseOptions?.global?.headers,
        // Header personnalisé pour identifier l'app source dans audit_log
        // Note: côté backend, ce header doit être lu pour setter le GUC `app.source`
        'X-CB-App-Source': appSource,
      },
    },
  });

  return client;
}
